"use strict";

(function () {
  const WAREHOUSE_ORDER = ["central", "partner"];
  const warehouseCache = new Map();

  function getWarehouseLabels() {
    return {
      central: window.cartStrings?.warehouseCentral || "Central Warehouse",
      partner: window.cartStrings?.warehousePartner || "Partner Warehouse",
    };
  }

  function getMultiWarehouseMessage() {
    return (
      window.cartStrings?.multiWarehouseMessage ||
      "Your order ships from multiple warehouses to get to you faster"
    );
  }

  function normalizeWarehouseKey(value) {
    return value === "central" || value === "partner" ? value : "";
  }

  function parseWarehouseNodes(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const map = new Map();

    doc.querySelectorAll("[data-warehouse-key], [data-cart-warehouse-key]").forEach(function (node) {
      const variantId = node.dataset.variantId;
      const warehouseKey = normalizeWarehouseKey(node.dataset.warehouseKey);
      if (variantId && warehouseKey) {
        map.set(String(variantId), warehouseKey);
      }
    });

    return map;
  }

  function getProductUrlForVariant(variantId) {
    const variantIdString = String(variantId);
    const item = Array.from(document.querySelectorAll("[data-variant-id]")).find(
      (element) => element.dataset.variantId === variantIdString,
    );

    return item?.dataset.productUrl || "";
  }

  function buildVariantWarehouseLookupUrls(variantId, productUrl) {
    const urls = [];
    const cacheBuster = `_=${Date.now()}`;

    urls.push(`${window.Shopify?.routes?.cart_url || "/cart"}?section_id=cart-warehouse-keys&${cacheBuster}`);

    if (productUrl) {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("variant", variantId);
      url.searchParams.set("section_id", "variant-warehouse-key");
      url.searchParams.set("_", String(Date.now()));
      urls.push(url.toString());
    }

    const rootUrl = window.Shopify?.routes?.root || "/";
    const normalizedRoot = rootUrl.endsWith("/") ? rootUrl : `${rootUrl}/`;
    urls.push(
      `${normalizedRoot}variants/${variantId}/?section_id=variant-warehouse-key&${cacheBuster}`,
    );

    return urls;
  }

  async function fetchWarehouseMapFromUrl(url) {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`Warehouse lookup failed (${response.status})`);
    }

    const text = await response.text();
    return parseWarehouseNodes(text);
  }

  async function fetchVariantWarehouseKey(variantId, productUrl = "") {
    if (!variantId) return "";

    const variantIdString = String(variantId);
    const resolvedProductUrl = productUrl || getProductUrlForVariant(variantId);
    const cacheKey = `${variantIdString}:${resolvedProductUrl}`;

    if (warehouseCache.has(cacheKey)) {
      return warehouseCache.get(cacheKey);
    }

    const urls = buildVariantWarehouseLookupUrls(variantIdString, resolvedProductUrl);

    try {
      for (const url of urls) {
        const warehouseMap = await fetchWarehouseMapFromUrl(url);
        const warehouseKey = warehouseMap.get(variantIdString) || "";

        if (warehouseKey) {
          warehouseCache.set(cacheKey, warehouseKey);
          return warehouseKey;
        }
      }

      warehouseCache.set(cacheKey, "central");
      return "central";
    } catch (error) {
      console.warn("Unable to resolve warehouse for variant", variantId, error);
      return "central";
    }
  }

  async function resolveItemWarehouses(items) {
    const unresolvedItems = items.filter((item) => item.dataset.variantId);
    const resolvedProductUrls = unresolvedItems
      .map((item) => item.dataset.productUrl || "")
      .join("|");
    const batchCacheKey = `batch:${unresolvedItems.map((item) => item.dataset.variantId).join(",")}:${resolvedProductUrls}`;

    let batchMap = warehouseCache.get(batchCacheKey);

    if (!batchMap) {
      batchMap = new Map();

      try {
        const cartUrl = `${window.Shopify?.routes?.cart_url || "/cart"}?section_id=cart-warehouse-keys&_=${Date.now()}`;
        const cartMap = await fetchWarehouseMapFromUrl(cartUrl);
        cartMap.forEach(function (value, key) {
          batchMap.set(key, value);
        });
      } catch (error) {
        console.warn("Unable to resolve warehouses from cart section", error);
      }

      if (batchMap.size > 0) {
        warehouseCache.set(batchCacheKey, batchMap);
      }
    }

    const tasks = items.map(async (item) => {
      const variantId = item.dataset.variantId;
      const existing = item.dataset.warehouse;

      if (!variantId) {
        return existing === "central" || existing === "partner" ? existing : "";
      }

      const variantIdString = String(variantId);
      let warehouseKey = batchMap?.get(variantIdString) || "";

      if (!warehouseKey) {
        warehouseKey = await fetchVariantWarehouseKey(variantIdString, item.dataset.productUrl);
      }

      if (!warehouseKey && (existing === "central" || existing === "partner")) {
        warehouseKey = existing;
      }

      if (!warehouseKey) {
        warehouseKey = "central";
      }

      if (warehouseKey) {
        item.dataset.warehouse = warehouseKey;
        if (window.CartQuantityLimit) {
          window.CartQuantityLimit.variantWarehouseMap.set(variantIdString, warehouseKey);
        }
      } else {
        item.removeAttribute("data-warehouse");
      }

      item.removeAttribute("data-needs-warehouse-resolve");
      return warehouseKey;
    });

    return Promise.all(tasks);
  }

  function formatMoney(cents) {
    const amount = Number(cents) / 100;
    const currency = window.Shopify?.currency?.active || "USD";

    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency,
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function getItemDisplayQuantity(item) {
    const input = item.querySelector('[name="updates[]"]');
    const held =
      typeof window.CartQuantityLimit?.getHeldQtyForItem === "function"
        ? window.CartQuantityLimit.getHeldQtyForItem(item)
        : null;
    const committed = parseInt(item.dataset.quantity || "0", 10) || 0;
    const typed = parseInt(input?.value || "0", 10) || 0;
    return Math.max(held || 0, typed, committed) || 1;
  }

  function getItemUnitPriceCents(item) {
    const unitPrice = parseInt(item.dataset.unitPrice || "0", 10);
    if (unitPrice > 0) return unitPrice;

    const serverQty =
      parseInt(item.dataset.serverQuantity || item.dataset.quantity || "0", 10) || 0;
    const linePrice = parseInt(item.dataset.linePrice || "0", 10) || 0;
    if (serverQty > 0 && linePrice > 0) {
      return Math.round(linePrice / serverQty);
    }
    return linePrice;
  }

  function computeWarehouseTotals(groupItems) {
    let subtotal = 0;
    let quantity = 0;
    const checkoutParts = [];

    groupItems.forEach(function (item) {
      const itemQuantity = getItemDisplayQuantity(item);
      const unitPrice = getItemUnitPriceCents(item);
      // Project line total from entered/held qty (error state keeps typed qty).
      subtotal += unitPrice * itemQuantity;
      quantity += itemQuantity;

      if (item.dataset.variantId) {
        checkoutParts.push(`${item.dataset.variantId}:${itemQuantity}`);
      }
    });

    return { subtotal, quantity, checkoutParts };
  }

  function syncHeldLineMoneyDisplays(item) {
    const qty = getItemDisplayQuantity(item);
    const unitPrice = getItemUnitPriceCents(item);
    const lineCents = unitPrice * qty;

    item.dataset.quantity = String(qty);
    item.dataset.linePrice = String(lineCents);

    const formatted = formatMoney(lineCents);
    item.querySelectorAll(".price.price-end").forEach(function (el) {
      if (el.closest("s") || el.classList.contains("cart-item-old-price")) return;
      el.textContent = formatted;
    });
  }

  /** Refresh warehouse subtotals (+ line totals) from held/typed qty — keeps error-state totals. */
  function getDisplayedCartItemCount() {
    const mainScope = document.querySelector("#main-cart-items .js-contents");
    const miniScope = document.querySelector("#header-mini-cart-content .drawer-content-inner");
    const scope =
      mainScope?.querySelector(".js-cart-item")
        ? mainScope
        : miniScope?.querySelector(".js-cart-item")
          ? miniScope
          : document;

    let total = 0;
    const seen = new Set();

    scope.querySelectorAll(".js-cart-item").forEach(function (item) {
      const key =
        item.querySelector('[name="updates[]"]')?.dataset?.key || item.id || item.dataset.variantId;
      if (!key || seen.has(String(key))) return;
      seen.add(String(key));
      total += getItemDisplayQuantity(item);
    });

    return total;
  }

  function refreshCartCountBadge() {
    const count = getDisplayedCartItemCount();

    document.querySelectorAll(".head-slot-cart-link-wrap").forEach(function (wrap) {
      let badge = wrap.querySelector(".head-slot-cart-link-quantity");

      if (count > 0) {
        if (!badge) {
          badge = document.createElement("span");
          badge.className = "head-slot-cart-link-quantity";
          wrap.appendChild(badge);
        }
        badge.hidden = false;
        badge.textContent = String(count);
      } else if (badge) {
        badge.textContent = "0";
        badge.hidden = true;
      }
    });
  }

  function refreshCartWarehouseSummaries() {
    // Prefer one cart root so we don't double-apply money on main + mini duplicates.
    const mainScope = document.querySelector("#main-cart-items .js-contents");
    const miniScope = document.querySelector("#header-mini-cart-content .drawer-content-inner");
    const moneyScope =
      mainScope?.querySelector(".js-cart-item")
        ? mainScope
        : miniScope?.querySelector(".js-cart-item")
          ? miniScope
          : document;

    moneyScope.querySelectorAll(".js-cart-item").forEach(syncHeldLineMoneyDisplays);

    document.querySelectorAll("[data-warehouse-group]").forEach(function (group) {
      const warehouseKey = group.dataset.warehouseGroup;
      const items = Array.from(group.querySelectorAll(".js-cart-item"));
      if (!items.length) return;

      const { subtotal, quantity } = computeWarehouseTotals(items);
      const valueEl = group.querySelector(
        `[data-warehouse-summary="${warehouseKey}"] .cart-warehouse-summary__value`,
      );
      if (valueEl) {
        valueEl.textContent = formatMoney(subtotal);
      }

      const checkoutDisabled = quantity > 10;
      const alert = group.querySelector(`[data-warehouse-alert="${warehouseKey}"]`);
      if (alert) {
        alert.hidden = !checkoutDisabled;
        alert.setAttribute("aria-hidden", checkoutDisabled ? "false" : "true");
      }

      const checkoutButton = group.querySelector(`[data-warehouse-checkout="${warehouseKey}"]`);
      if (checkoutButton) {
        checkoutButton.classList.toggle("button--disabled", checkoutDisabled);
        if (checkoutDisabled) {
          checkoutButton.setAttribute("aria-disabled", "true");
          checkoutButton.setAttribute("tabindex", "-1");
        } else {
          checkoutButton.removeAttribute("aria-disabled");
          checkoutButton.removeAttribute("tabindex");
        }
      }
    });

    // Header/mobile cart badge must include held over-limit qty (24 not Shopify's 20).
    refreshCartCountBadge();
  }

  window.refreshCartWarehouseSummaries = refreshCartWarehouseSummaries;

  /**
   * AZP-7380: over-limit banner for the cart preview's multi-warehouse layout.
   * Mini-cart only — the cart page warns with the AZP-7304 popup alone.
   */
  function buildMiniWarehouseQuantityAlert(warehouseKey, checkoutDisabled) {
    const alert = document.createElement("div");
    alert.className = "mini-cart-quantity-limit-alert";
    alert.dataset.warehouseAlert = warehouseKey;
    alert.setAttribute("role", "alert");
    alert.hidden = !checkoutDisabled;
    alert.setAttribute("aria-hidden", checkoutDisabled ? "false" : "true");

    const icon = document.createElement("span");
    icon.className = "mini-cart-quantity-limit-alert__icon";
    icon.setAttribute("aria-hidden", "true");
    icon.innerHTML =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#F57F00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const message = document.createElement("p");
    message.textContent =
      window.cartStrings?.quantityLimit ||
      "You can only add up to 10 items per order. Please remove some items before checking out";

    alert.appendChild(icon);
    alert.appendChild(message);
    return alert;
  }

  function buildWarehouseSummary(warehouseKey, groupItems, context) {
    const { subtotal, quantity, checkoutParts } = computeWarehouseTotals(groupItems);
    // Cap is per warehouse group — warehouse A at 10+ does not block warehouse B.
    const checkoutDisabled = quantity > 10;
    const cartUrl = window.Shopify?.routes?.cart_url || "/cart";
    const checkoutUrl = checkoutParts.length
      ? `${cartUrl}/${checkoutParts.join(",")}?checkout`
      : cartUrl;

    const isPage = context !== "mini";

    const summary = document.createElement("div");
    summary.className = `cart-warehouse-summary${isPage ? " cart-warehouse-summary--page" : " mini-cart-warehouse-summary"}`;
    summary.dataset.warehouseSummary = warehouseKey;

    const row = document.createElement("div");
    row.className = "cart-warehouse-summary__row";

    const label = document.createElement("span");
    label.className = "cart-warehouse-summary__label";
    // Mirrors snippets/cart-warehouse-summary.liquid: page says "Subtotal", mini says "Total".
    label.textContent = isPage
      ? window.cartStrings?.subtotal || "Subtotal"
      : window.cartStrings?.total || "Total";

    const value = document.createElement("span");
    value.className = "cart-warehouse-summary__value";
    value.textContent = formatMoney(subtotal);

    row.appendChild(label);
    row.appendChild(value);
    summary.appendChild(row);

    const note = document.createElement("p");
    note.className = "cart-warehouse-summary__note";
    if (isPage) {
      note.innerHTML =
        window.cartStrings?.taxesAndShippingPolicyHtml ||
        'Taxes and <a href="/policies/shipping-policy">shipping</a> calculated at checkout';
    } else {
      note.textContent =
        window.cartStrings?.taxesAndShippingAtCheckout ||
        "Taxes and shipping calculated at checkout";
    }
    summary.appendChild(note);

    if (!isPage) {
      summary.appendChild(buildMiniWarehouseQuantityAlert(warehouseKey, checkoutDisabled));
    }

    const checkoutButton = document.createElement("a");
    checkoutButton.href = checkoutUrl;
    checkoutButton.className = `button cart-warehouse-checkout-button${checkoutDisabled ? " button--disabled" : ""}`;
    checkoutButton.dataset.warehouseCheckout = warehouseKey;
    checkoutButton.textContent = isPage
      ? window.cartStrings?.proceedToCheckout || "Proceed to Checkout"
      : window.cartStrings?.checkout || "Checkout";

    if (checkoutDisabled) {
      checkoutButton.setAttribute("aria-disabled", "true");
      checkoutButton.setAttribute("tabindex", "-1");
    }

    const actions = document.createElement("div");
    actions.className = "cart-warehouse-summary__actions";
    actions.appendChild(checkoutButton);

    summary.appendChild(actions);

    if (isPage) {
      const trust = buildCartTrustBlock();
      if (trust) summary.appendChild(trust);
    }

    return summary;
  }

  // Cloned from the server-rendered card so the payment SVGs stay in Liquid.
  function buildCartTrustBlock() {
    const trust = document.querySelector("#main-cart-items .cart-warehouse-summary__trust");
    return trust ? trust.cloneNode(true) : null;
  }

  // Mirrors snippets/cart-page-items-body.liquid: table card beside summary card.
  function buildPageGroupLayout(table, summary) {
    const layout = document.createElement("div");
    layout.className = "cart-warehouse-group__layout";

    const itemsCard = document.createElement("div");
    itemsCard.className = "cart-warehouse-group__items";
    itemsCard.appendChild(table);

    layout.appendChild(itemsCard);
    layout.appendChild(summary);
    return layout;
  }

  function isAlreadyGrouped(container, warehousesPresent) {
    if (!warehousesPresent.size) return true;

    const summaries = container.querySelectorAll("[data-warehouse-summary]");
    if (summaries.length !== warehousesPresent.size) return false;

    return WAREHOUSE_ORDER.every(function (warehouseKey) {
      if (!warehousesPresent.has(warehouseKey)) return true;
      return Boolean(
        container.querySelector(
          `[data-warehouse-group="${warehouseKey}"] [data-warehouse-summary="${warehouseKey}"]`,
        ),
      );
    });
  }

  function buildCartTableHead() {
    const table = document.querySelector("#main-cart-items .cart-items thead tr");
    if (!table) return null;
    return table.cloneNode(true);
  }

  function groupMainCartItems(container, items, warehousesPresent) {
    const labels = getWarehouseLabels();
    const isMultiWarehouse = warehousesPresent.size > 1;
    const tableHeadRow = buildCartTableHead();
    const fragment = document.createDocumentFragment();
    const assignedItems = new Set();

    WAREHOUSE_ORDER.forEach(function (warehouseKey) {
      if (!warehousesPresent.has(warehouseKey)) return;

      const groupItems = items.filter((item) => item.dataset.warehouse === warehouseKey);
      if (!groupItems.length) return;

      groupItems.forEach(function (item) {
        assignedItems.add(item);
      });

      const group = document.createElement("div");
      group.className = `cart-warehouse-group cart-warehouse-group--boxed${isMultiWarehouse ? "" : " cart-warehouse-group--single"}`;
      group.dataset.warehouseGroup = warehouseKey;

      if (isMultiWarehouse) {
        const heading = document.createElement("h2");
        heading.className = "cart-warehouse-heading";
        heading.textContent = labels[warehouseKey] || warehouseKey;
        group.appendChild(heading);
      }

      const table = document.createElement("table");
      table.className = "cart-items";

      if (tableHeadRow) {
        const thead = document.createElement("thead");
        thead.appendChild(tableHeadRow.cloneNode(true));
        table.appendChild(thead);
      }

      const tbody = document.createElement("tbody");
      groupItems.forEach(function (item) {
        tbody.appendChild(item);
      });
      table.appendChild(tbody);
      group.appendChild(
        buildPageGroupLayout(table, buildWarehouseSummary(warehouseKey, groupItems, "page")),
      );
      fragment.appendChild(group);
    });

    const orphanItems = items.filter(function (item) {
      return !assignedItems.has(item);
    });

    if (orphanItems.length) {
      orphanItems.forEach(function (item) {
        item.dataset.warehouse = "central";
      });
      warehousesPresent.add("central");

      const group = document.createElement("div");
      group.className = `cart-warehouse-group cart-warehouse-group--boxed${isMultiWarehouse ? "" : " cart-warehouse-group--single"}`;
      group.dataset.warehouseGroup = "central";

      if (isMultiWarehouse) {
        const heading = document.createElement("h2");
        heading.className = "cart-warehouse-heading";
        heading.textContent = labels.central || "central";
        group.appendChild(heading);
      }

      const table = document.createElement("table");
      table.className = "cart-items";

      if (tableHeadRow) {
        const thead = document.createElement("thead");
        thead.appendChild(tableHeadRow.cloneNode(true));
        table.appendChild(thead);
      }

      const tbody = document.createElement("tbody");
      orphanItems.forEach(function (item) {
        tbody.appendChild(item);
      });
      table.appendChild(tbody);
      group.appendChild(
        buildPageGroupLayout(table, buildWarehouseSummary("central", orphanItems, "page")),
      );
      fragment.appendChild(group);
    }

    container.replaceChildren(fragment);
  }

  function groupMiniCartItems(container, items, warehousesPresent) {
    const labels = getWarehouseLabels();
    const isMultiWarehouse = warehousesPresent.size > 1;
    const fragment = document.createDocumentFragment();
    const assignedItems = new Set();

    WAREHOUSE_ORDER.forEach(function (warehouseKey) {
      if (!warehousesPresent.has(warehouseKey)) return;

      const groupItems = items.filter((item) => item.dataset.warehouse === warehouseKey);
      if (!groupItems.length) return;

      groupItems.forEach(function (item) {
        assignedItems.add(item);
      });

      const group = document.createElement("div");
      group.className = `cart-warehouse-group mini-cart-warehouse-group${isMultiWarehouse ? "" : " cart-warehouse-group--single"}`;
      group.dataset.warehouseGroup = warehouseKey;

      if (isMultiWarehouse) {
        const heading = document.createElement("h2");
        heading.className = "cart-warehouse-heading";
        heading.textContent = labels[warehouseKey] || warehouseKey;
        group.appendChild(heading);
      }

      const list = document.createElement("div");
      list.className = "mini-cart-item-list";
      groupItems.forEach(function (item) {
        list.appendChild(item);
      });
      group.appendChild(list);
      group.appendChild(buildWarehouseSummary(warehouseKey, groupItems, "mini"));
      fragment.appendChild(group);
    });

    const orphanItems = items.filter(function (item) {
      return !assignedItems.has(item);
    });

    if (orphanItems.length) {
      orphanItems.forEach(function (item) {
        item.dataset.warehouse = "central";
      });

      const group = document.createElement("div");
      group.className = `cart-warehouse-group mini-cart-warehouse-group${isMultiWarehouse ? "" : " cart-warehouse-group--single"}`;
      group.dataset.warehouseGroup = "central";

      if (isMultiWarehouse) {
        const heading = document.createElement("h2");
        heading.className = "cart-warehouse-heading";
        heading.textContent = labels.central || "central";
        group.appendChild(heading);
      }

      const list = document.createElement("div");
      list.className = "mini-cart-item-list";
      orphanItems.forEach(function (item) {
        list.appendChild(item);
      });
      group.appendChild(list);
      group.appendChild(buildWarehouseSummary("central", orphanItems, "mini"));
      fragment.appendChild(group);
    }

    container.replaceChildren(fragment);
  }

  function ensureMultiWarehouseMessage(selector, className) {
    const host = document.querySelector(selector);
    if (!host) return;

    let message = host.querySelector(`.${className.split(" ").join(".")}`);
    if (!message) {
      message = document.createElement("p");
      message.className = className;
      message.textContent = getMultiWarehouseMessage();
      host.appendChild(message);
    } else {
      message.hidden = false;
    }
  }

  function removeMultiWarehouseMessage(selector, className) {
    document.querySelectorAll(`${selector} .${className.split(" ").join(".")}`).forEach(function (node) {
      node.remove();
    });
  }

  function updateMultiWarehouseChrome(isMultiWarehouse) {
    const mainCartFooter = document.getElementById("main-cart-footer");
    const miniCartFooter = document.getElementById("header-mini-cart-footer");
    const hasWarehouseLayout = Boolean(
      document.querySelector("[data-warehouse-group]"),
    );

    if (mainCartFooter) {
      mainCartFooter.classList.toggle("cart-footer--multi-warehouse", hasWarehouseLayout);
    }

    if (miniCartFooter) {
      miniCartFooter.classList.toggle(
        "header-mini-cart-footer--multi-warehouse",
        hasWarehouseLayout,
      );
      // AZP-7080: single warehouse keeps Total + View Cart + Checkout in the sticky
      // footer; multi warehouse moves the totals into each group and leaves View Cart.
      miniCartFooter.classList.toggle(
        "header-mini-cart-footer--single-warehouse",
        hasWarehouseLayout && !isMultiWarehouse,
      );
    }

    const miniCartHeader = document.querySelector(".mini-cart-header");
    if (miniCartHeader) {
      miniCartHeader.classList.toggle("mini-cart-header--multi-warehouse", isMultiWarehouse);
    }

    if (isMultiWarehouse) {
      ensureMultiWarehouseMessage(
        "#cart-page-title-wrap .cart-page-title-group",
        "cart-multi-warehouse-message cart-page-multi-warehouse-message",
      );
      ensureMultiWarehouseMessage(
        ".mini-cart-header__title-wrap",
        "mini-cart-multi-warehouse-message mini-cart-multi-warehouse-message--header",
      );
    } else {
      removeMultiWarehouseMessage("#cart-page-title-wrap", "cart-page-multi-warehouse-message");
      removeMultiWarehouseMessage(
        ".mini-cart-header__title-wrap",
        "mini-cart-multi-warehouse-message--header",
      );
    }
  }

  async function resolveContainer(container) {
    if (!container) return;

    const items = Array.from(container.querySelectorAll(".js-cart-item"));
    if (!items.length) return;

    container.classList.add("cart-warehouse-resolving");

    try {
      const warehouses = await resolveItemWarehouses(items);
      const warehousesPresent = new Set(warehouses.filter(Boolean));
      const isMultiWarehouse = warehousesPresent.size > 1;

      updateMultiWarehouseChrome(isMultiWarehouse);

      if (!warehousesPresent.size) return;

      if (isAlreadyGrouped(container, warehousesPresent)) {
        // Still refresh limit UI — do not leave stale/hidden alerts from a prior sync.
        if (window.CartQuantityLimit) {
          window.CartQuantityLimit.applyCartQuantityLimitUIFromDom(document);
        }
        return;
      }

      if (container.closest("#main-cart-items")) {
        groupMainCartItems(container, items, warehousesPresent);
      } else {
        groupMiniCartItems(container, items, warehousesPresent);
      }
    } finally {
      container.classList.remove("cart-warehouse-resolving");
    }
  }

  let resolveCartWarehousesInFlight = null;

  async function resolveCartWarehouses() {
    if (resolveCartWarehousesInFlight) {
      return resolveCartWarehousesInFlight;
    }

    resolveCartWarehousesInFlight = (async function () {
      // Restore persisted over-limit qtys before regroup so totals/badge survive refresh.
      if (window.CartQuantityLimit) {
        window.CartQuantityLimit.loadHeldFromStorage();
        window.CartQuantityLimit.remapHeldKeysFromDom(document);
        window.CartQuantityLimit.applyHeldQuantitiesToDom(document);
      }

      const tasks = [];

      document
        .querySelectorAll("#main-cart-items .js-contents, #header-mini-cart-content .drawer-content-inner")
        .forEach(function (container) {
          if (container.querySelector(".js-cart-item")) {
            tasks.push(resolveContainer(container));
          }
        });

      await Promise.all(tasks);

      if (typeof window.refreshCartWarehouseSummaries === "function") {
        window.refreshCartWarehouseSummaries();
      }

      if (window.CartQuantityLimit) {
        window.CartQuantityLimit.applyCartQuantityLimitUIFromDom(document);
      }

      stripInjectedCartReviewBadges(document);
      applyAppioRatingsToCartRatings(document).catch(function (error) {
        console.warn("Unable to refresh cart review ratings", error);
      });
    })();

    try {
      return await resolveCartWarehousesInFlight;
    } finally {
      resolveCartWarehousesInFlight = null;
    }
  }

  window.fetchVariantWarehouseKey = fetchVariantWarehouseKey;
  window.resolveCartWarehouses = resolveCartWarehouses;

  /** Keep only our cart rating block; drop duplicate/injected badges outside it. */
  function stripInjectedCartReviewBadges(root) {
    const scope = root || document;

    scope.querySelectorAll(".cart-item-details, .mini-cart-item-info").forEach(function (details) {
      const title =
        details.querySelector(".cart-item-name") ||
        details.querySelector(".mini-cart-item-heading");
      const price =
        details.querySelector(".cart-item-unit-price") ||
        details.querySelector(".mini-cart-item-price");
      if (!title) return;

      let node = title.nextElementSibling;
      let keptOurRating = false;

      while (node && node !== price) {
        const next = node.nextElementSibling;
        const isOurRating =
          node.matches &&
          (node.matches("[data-azparts-cart-rating]") ||
            node.matches(".cart-line-item-rating"));

        if (isOurRating && !keptOurRating) {
          keptOurRating = true;
        } else {
          // Duplicate Liquid rating OR any injected review badge (Judge.me / Stamped / Yotpo / …)
          node.remove();
        }
        node = next;
      }

      // Also remove badges nested elsewhere in the line item (not only direct siblings).
      details
        .querySelectorAll(
          ".jdgm-widget, .jdgm-prev-badge, .jdgm-preview-badge, .stamped-product-reviews-badge, .yotpo-widget-instance, [class*='jdgm-']",
        )
        .forEach(function (el) {
          if (el.closest("[data-azparts-cart-rating]")) return;
          el.remove();
        });
    });
  }

  function buildCartRatingStarSvg(kind) {
    const cls =
      kind === "half"
        ? "icon-rating-star icon-rating-star-half"
        : kind === "empty"
          ? "icon-rating-star icon-rating-star-empty"
          : "icon-rating-star icon-rating-star-full";
    const fill =
      kind === "half"
        ? 'fill="url(#rating-star-gradient-half)"'
        : 'fill="currentColor"';
    return (
      '<svg fill="none" focusable="false" role="presentation" class="' +
      cls +
      '" viewBox="0 0 896 832">' +
      '<path d="M896 320l-313.5-40.781L448 0 313.469 279.219 0 320l230.469 208.875L171 831.938l277-148.812 277.062 148.812L665.5 528.875z" fill-rule="nonzero" ' +
      fill +
      "></path></svg>"
    );
  }

  function renderCartRatingStars(starsEl, ratingValue) {
    if (!starsEl) return;
    let value = Number(ratingValue);
    if (!Number.isFinite(value) || value <= 0) return;

    const scaleMax = 5;
    let floor = Math.floor(value);
    if (floor < 0) floor = 0;
    if (floor > scaleMax) floor = scaleMax;
    const decimal = value - floor;
    let showHalf = false;
    if (decimal >= 0.3 && decimal <= 0.7 && floor < scaleMax) showHalf = true;
    if (decimal > 0.7 && floor < scaleMax) floor += 1;

    let html = "";
    for (let i = 1; i <= scaleMax; i += 1) {
      if (i <= floor) html += buildCartRatingStarSvg("full");
      else if (showHalf && i === floor + 1) html += buildCartRatingStarSvg("half");
      else html += buildCartRatingStarSvg("empty");
    }
    starsEl.innerHTML = html;
  }

  /**
   * Live product rating for cart line items — mirrored from the exact Appio
   * (Amazon Reviews) badge already rendered on THIS page, not a separate
   * fetch. The Shopify reviews.* metafield the Liquid fallback uses is never
   * written by Appio, so it can disagree with what the shopper just saw on
   * the PDP; copying the on-page badge's own data-rating/data-raters
   * guarantees the cart shows the identical number, byte for byte.
   *
   * Only falls back to calling Appio's own endpoint (same one its script
   * uses to fill these badges) when no matching badge exists anywhere on
   * the current page — e.g. the shopper is on the cart page itself with an
   * item that isn't the current page's product and isn't shown in any
   * on-page card.
   */
  function getCurrentPageProductId() {
    try {
      const id = window.ShopifyAnalytics?.meta?.product?.id;
      return id != null ? String(id) : null;
    } catch (e) {
      return null;
    }
  }

  function readAppioBadgeData(el) {
    if (!el) return null;
    const rating = Number(el.getAttribute("data-rating"));
    const raters = Number(el.getAttribute("data-raters"));
    if (!Number.isFinite(rating) || !Number.isFinite(raters) || raters <= 0) return null;
    return { rating: rating, raters: raters };
  }

  /**
   * Collection/search cards render the theme's own metafield-based rating
   * widget (snippets/rating.liquid), not an Appio badge — so on those pages
   * "the exact same product outside the cart" means that card's own
   * `.star-rating-link`, not Appio. Clone its data-average-rating + parsed
   * review count verbatim so the cart matches whatever the shopper already
   * saw on that card, whichever source rendered it.
   */
  function findThemeCardRatingForProduct(productId) {
    const id = String(productId);
    const link = document.querySelector(
      '[data-product-id="' + id + '"] .star-rating-link[data-average-rating]',
    );
    if (!link) return null;

    const captionEl = link.querySelector(".rating-caption");
    const countMatch = captionEl && captionEl.textContent.match(/\((\d+)\)/);
    const raters = countMatch ? Number(countMatch[1]) : NaN;
    if (!Number.isFinite(raters) || raters <= 0) return null;

    const rating = Number(link.getAttribute("data-average-rating"));
    return { rating: Number.isFinite(rating) ? rating : 0, raters: raters };
  }

  function findOnPageRatingForProduct(productId) {
    const id = String(productId);

    // The PDP's own Appio "major" badge carries no data-id — it implicitly
    // refers to whichever product this page is for.
    if (getCurrentPageProductId() === id) {
      const major = readAppioBadgeData(
        document.querySelector(".appio-rating-major[data-rating][data-raters]"),
      );
      if (major) return major;
    }

    // Any other Appio badge on the page explicitly tagged for this product
    // (collection grids with Appio's own badge-on-list feature enabled).
    const appioTagged = readAppioBadgeData(
      document.querySelector(
        '.appio-rating-badge[data-id="' + id + '"][data-rating][data-raters]',
      ),
    );
    if (appioTagged) return appioTagged;

    // Theme's own rating widget on a product card elsewhere on the page.
    return findThemeCardRatingForProduct(id);
  }

  const appioRatingFetchCache = new Map();

  async function fetchAppioRatings(productIds) {
    const result = new Map();
    if (!productIds.length) return result;

    try {
      const response = await fetch("/apps/appio/review/get-products-ratings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productIds: productIds,
          productHandles: [],
          locale: document.documentElement.lang || "en",
          useImportSummary: false,
          saveRatings: false,
        }),
      });
      if (!response.ok) return result;

      const data = await response.json();
      Object.keys(data || {}).forEach(function (handle) {
        const entry = data[handle];
        if (entry && entry.productId != null) {
          result.set(String(entry.productId), {
            rating: Number(entry.rating),
            raters: Number(entry.raters),
          });
        }
      });
    } catch (error) {
      console.warn("Unable to fetch Appio ratings for cart", error);
    }

    return result;
  }

  function applyRatingToWrap(wrap, parsed) {
    if (!parsed) return;
    const caption = wrap.querySelector(".cart-line-item-rating__caption");
    if (caption) {
      const reviewLabel = caption.getAttribute("data-reviews-label") || "reviews";
      caption.textContent = "(" + parsed.raters + " " + reviewLabel + ")";
    }
    wrap.setAttribute("data-average-rating", String(parsed.rating));
    renderCartRatingStars(wrap.querySelector(".cart-line-item-rating__stars"), parsed.rating);
    wrap.classList.remove("cart-line-item-rating--pending");
  }

  async function applyAppioRatingsToCartRatings(root) {
    const scope = root || document;
    const wraps = Array.from(scope.querySelectorAll("[data-azparts-cart-rating]"));
    if (!wraps.length) return;

    const stillNeeded = [];
    wraps.forEach(function (wrap) {
      const id = wrap.getAttribute("data-product-id");
      if (!id) return;
      const onPage = findOnPageRatingForProduct(id);
      if (onPage) {
        applyRatingToWrap(wrap, onPage);
        return;
      }
      if (appioRatingFetchCache.has(id)) {
        applyRatingToWrap(wrap, appioRatingFetchCache.get(id));
        return;
      }
      if (stillNeeded.indexOf(id) === -1) stillNeeded.push(id);
    });

    if (!stillNeeded.length) return;

    const fetched = await fetchAppioRatings(stillNeeded);
    stillNeeded.forEach(function (id) {
      appioRatingFetchCache.set(id, fetched.get(id) || null);
    });

    wraps.forEach(function (wrap) {
      const id = wrap.getAttribute("data-product-id");
      if (id && stillNeeded.indexOf(id) !== -1) {
        applyRatingToWrap(wrap, appioRatingFetchCache.get(id));
      }
    });
  }

  function syncCartReviewBadges(root) {
    stripInjectedCartReviewBadges(root);
    applyAppioRatingsToCartRatings(root || document).catch(function (error) {
      console.warn("Unable to refresh cart review ratings", error);
    });
  }

  window.stripInjectedCartReviewBadges = stripInjectedCartReviewBadges;
  window.applyAppioRatingsToCartRatings = applyAppioRatingsToCartRatings;
  window.syncCartReviewBadges = syncCartReviewBadges;

  let cartReviewStripObserver = null;
  let cartReviewStripTimer = null;

  function scheduleStripInjectedCartReviewBadges() {
    if (cartReviewStripTimer) return;
    cartReviewStripTimer = window.setTimeout(function () {
      cartReviewStripTimer = null;
      stripInjectedCartReviewBadges(document);
    }, 50);
  }

  function startCartReviewStripObserver() {
    if (cartReviewStripObserver || typeof MutationObserver === "undefined") return;

    const targets = [
      document.getElementById("main-cart-items"),
      document.getElementById("header-mini-cart-content"),
    ].filter(Boolean);
    if (!targets.length) return;

    cartReviewStripObserver = new MutationObserver(function () {
      scheduleStripInjectedCartReviewBadges();
    });

    targets.forEach(function (target) {
      cartReviewStripObserver.observe(target, { childList: true, subtree: true });
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    syncCartReviewBadges(document);
    startCartReviewStripObserver();
    resolveCartWarehouses().finally(function () {
      syncCartReviewBadges(document);
    });
  });
  ["cart:item-add", "cart:item-remove", "cart:item-quantity-update"].forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      requestAnimationFrame(function () {
        resolveCartWarehouses().finally(function () {
          syncCartReviewBadges(document);
        });
      });
    });
  });

  [500, 1500, 3000, 5000].forEach(function (ms) {
    window.setTimeout(function () {
      syncCartReviewBadges(document);
    }, ms);
  });

  // Re-sync ratings whenever the drawer/mini-cart opens (cache makes repeats cheap).
  if (typeof MutationObserver !== "undefined") {
    const drawerOpenObserver = new MutationObserver(function () {
      if (
        document.body.classList.contains("drawer-open") ||
        document.body.classList.contains("header-mini-cart-open")
      ) {
        window.setTimeout(function () {
          applyAppioRatingsToCartRatings(document).catch(function (error) {
            console.warn("Unable to refresh cart review ratings", error);
          });
        }, 200);
      }
    });
    drawerOpenObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  const CHECKOUT_SNAPSHOT_KEY = "azparts:warehouse-checkout-snapshot";
  const PENDING_CHECKOUT_KEY = "azparts:pending-warehouse-checkout";
  const BASELINE_CART_KEY = "azparts:warehouse-cart-baseline";
  const CHECKOUT_NAVIGATION_KEY = "azparts:warehouse-checkout-navigation";
  // Note: RESTORE_LOCK_KEY intentionally removed — sessionStorage locks persist across
  // page navigations within the same tab, causing restores to be permanently blocked
  // if the tab navigates away while a restore is in flight. Use restoreCheckoutInFlight
  // (reset on each page load) as the sole concurrency guard.
  let restoreCheckoutInFlight = false;
  let restoreScheduled = false;

  function getNormalizedCartBasePath() {
    return (
      (window.Shopify?.routes?.cart_url || "/cart")
        .replace(/^https?:\/\/[^/]+/, "")
        .replace(/\/$/, "") || "/cart"
    );
  }

  function isOnWarehouseCartPermalinkPath() {
    try {
      const url = new URL(window.location.href);
      const cartBase = getNormalizedCartBasePath();
      const path = url.pathname.replace(/\/$/, "") || "/";

      return path.startsWith(`${cartBase}/`) && path.length > cartBase.length + 1;
    } catch (error) {
      return false;
    }
  }

  function isOnWarehousePartialCheckoutPage() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has("checkout")) {
        return false;
      }

      return isOnWarehouseCartPermalinkPath();
    } catch (error) {
      return false;
    }
  }

  function isCartLikePage() {
    return isCartPage() || isOnWarehousePartialCheckoutPage();
  }

  function markWarehouseCheckoutNavigation() {
    sessionStorage.setItem(CHECKOUT_NAVIGATION_KEY, String(Date.now()));
  }

  function clearWarehouseCheckoutNavigation() {
    sessionStorage.removeItem(CHECKOUT_NAVIGATION_KEY);
  }

  function shouldSkipRestoreDuringCheckoutNavigation() {
    if (isOnWarehousePartialCheckoutPage()) {
      return true;
    }

    const raw = sessionStorage.getItem(CHECKOUT_NAVIGATION_KEY);
    if (!raw) {
      return false;
    }

    const startedAt = parseInt(raw, 10);
    if (!Number.isFinite(startedAt)) {
      clearWarehouseCheckoutNavigation();
      return false;
    }

    const nav = performance.getEntriesByType("navigation")[0];
    if (nav && nav.type === "back_forward") {
      clearWarehouseCheckoutNavigation();
      return false;
    }

    // Forward navigation to /cart/{lines}?checkout — cart is mid-update; do not restore yet.
    if (Date.now() - startedAt < 3000) {
      return true;
    }

    clearWarehouseCheckoutNavigation();
    return false;
  }

  async function waitForRestoreToFinish() {
    const deadline = Date.now() + 10000;

    while (restoreCheckoutInFlight && Date.now() < deadline) {
      await new Promise(function (resolve) {
        window.setTimeout(resolve, 50);
      });
    }
  }

  function isCartPage() {
    const cartPath = (window.routes?.cart_url || window.Shopify?.routes?.cart_url || "/cart")
      .replace(/^https?:\/\/[^/]+/, "")
      .replace(/\/$/, "");
    const normalizedCartPath = cartPath || "/cart";
    const pathname = window.location.pathname.replace(/\/$/, "") || "/";

    return (
      pathname === normalizedCartPath ||
      pathname.endsWith(normalizedCartPath)
    );
  }

  function mergeCartItemLists() {
    const quantityByVariant = new Map();

    Array.from(arguments).forEach(function (itemList) {
      normalizeSnapshotItems(itemList).forEach(function (item) {
        quantityByVariant.set(
          item.id,
          Math.max(quantityByVariant.get(item.id) || 0, item.quantity),
        );
      });
    });

    return Array.from(quantityByVariant.entries()).map(function ([id, quantity]) {
      return { id, quantity };
    });
  }

  function readStoredSnapshotRaw() {
    return (
      sessionStorage.getItem(CHECKOUT_SNAPSHOT_KEY) ||
      localStorage.getItem(CHECKOUT_SNAPSHOT_KEY)
    );
  }

  function writeStoredSnapshotRaw(value) {
    if (value) {
      sessionStorage.setItem(CHECKOUT_SNAPSHOT_KEY, value);
      localStorage.setItem(CHECKOUT_SNAPSHOT_KEY, value);
      return;
    }

    sessionStorage.removeItem(CHECKOUT_SNAPSHOT_KEY);
    localStorage.removeItem(CHECKOUT_SNAPSHOT_KEY);
  }

  function writeStoredPending(value) {
    if (value) {
      sessionStorage.setItem(PENDING_CHECKOUT_KEY, value);
      localStorage.setItem(PENDING_CHECKOUT_KEY, value);
      return;
    }

    sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
    localStorage.removeItem(PENDING_CHECKOUT_KEY);
  }

  function readStoredPending() {
    return (
      sessionStorage.getItem(PENDING_CHECKOUT_KEY) ||
      localStorage.getItem(PENDING_CHECKOUT_KEY)
    );
  }

  function getCartItemsRoot() {
    const mainCartItems = document.querySelector("#main-cart-items .js-contents");
    if (mainCartItems?.querySelector(".js-cart-item")) {
      return mainCartItems;
    }

    const miniCartItems = document.querySelector("#header-mini-cart-content .drawer-content-inner");
    if (miniCartItems?.querySelector(".js-cart-item")) {
      return miniCartItems;
    }

    return null;
  }

  function collectCartItemsFromDom(warehouseKey) {
    const items = new Map();
    const root = getCartItemsRoot();
    if (!root) return [];

    const selector = warehouseKey
      ? `.js-cart-item[data-warehouse="${warehouseKey}"]`
      : ".js-cart-item";

    root.querySelectorAll(selector).forEach(function (item) {
      const variantId = parseInt(item.dataset.variantId, 10);
      // Prefer data-quantity (SSR/AJAX-rendered server value) over input.value
      // because input.value may reflect an unsaved user edit that hasn't been
      // submitted to Shopify yet, giving an inaccurate snapshot quantity.
      const input = item.querySelector('[name="updates[]"]');
      const quantity = parseInt(item.dataset.quantity || input?.value || "0", 10) || 0;

      if (!variantId || quantity <= 0) return;

      // Use max — grouped DOM can briefly contain duplicate nodes for one variant.
      items.set(variantId, Math.max(items.get(variantId) || 0, quantity));
    });

    return Array.from(items.entries()).map(function ([id, quantity]) {
      return { id, quantity };
    });
  }

  function parseWarehouseCheckoutItems(checkoutUrl) {
    const url = new URL(checkoutUrl, window.location.origin);
    const cartPath = url.pathname.replace(/\/$/, "");
    const cartBase = (window.Shopify?.routes?.cart_url || "/cart").replace(/\/$/, "");

    if (!cartPath.startsWith(`${cartBase}/`)) return [];

    const itemsSegment = cartPath.slice(cartBase.length + 1);
    if (!itemsSegment) return [];

    return itemsSegment.split(",").map(function (part) {
      const [variantId, quantity] = part.split(":");
      return {
        id: parseInt(variantId, 10),
        quantity: parseInt(quantity, 10) || 0,
      };
    }).filter(function (item) {
      return item.id && item.quantity > 0;
    });
  }

  function normalizeSnapshotItems(items) {
    const quantityByVariant = new Map();

    (items || []).forEach(function (item) {
      const variantId = parseInt(item.id, 10);
      const quantity = parseInt(item.quantity, 10) || 0;

      if (!variantId || quantity <= 0) return;

      quantityByVariant.set(
        variantId,
        Math.max(quantityByVariant.get(variantId) || 0, quantity),
      );
    });

    return Array.from(quantityByVariant.entries()).map(function ([id, quantity]) {
      return { id, quantity };
    });
  }

  function saveCheckoutSnapshot(snapshot) {
    const fullCart = normalizeSnapshotItems(snapshot.fullCart || snapshot.items || []);
    const checkoutItems = normalizeSnapshotItems(snapshot.checkoutItems || []);

    if (!fullCart.length) return;

    writeStoredSnapshotRaw(
      JSON.stringify({
        fullCart,
        checkoutItems,
        checkoutWarehouseKey: snapshot.checkoutWarehouseKey || "",
        savedAt: Date.now(),
      }),
    );
  }

  function markCheckoutPending() {
    writeStoredPending(String(Date.now()));
  }

  function getBaselineFullCart() {
    try {
      return normalizeSnapshotItems(JSON.parse(sessionStorage.getItem(BASELINE_CART_KEY) || "[]"));
    } catch (error) {
      return [];
    }
  }

  function rememberBaselineFullCart(items) {
    const merged = mergeCartItemLists(getBaselineFullCart(), items);
    if (!merged.length) return;
    sessionStorage.setItem(BASELINE_CART_KEY, JSON.stringify(merged));
  }

  function setBaselineFullCart(items) {
    const normalized = normalizeSnapshotItems(items);
    if (!normalized.length) {
      sessionStorage.removeItem(BASELINE_CART_KEY);
      return;
    }
    sessionStorage.setItem(BASELINE_CART_KEY, JSON.stringify(normalized));
  }

  function clearBaselineFullCart() {
    sessionStorage.removeItem(BASELINE_CART_KEY);
  }

  function getEffectiveFullCart(snapshot) {
    return mergeCartItemLists(getSnapshotFullCart(snapshot), getBaselineFullCart());
  }

  function isThankYouReferrer(referrer) {
    return /thank|order[-_]?status|post_purchase|orders\/\d/i.test(referrer || "");
  }

  function isCheckoutAbandonedReturn() {
    if (isLikelyAbandonedCheckoutReturn()) {
      return true;
    }

    const referrer = document.referrer || "";
    if (!referrer) {
      return false;
    }

    if (/checkout\.shopify\.com/i.test(referrer) && !isThankYouReferrer(referrer)) {
      return true;
    }

    if (/\/checkouts\//i.test(referrer) && !isThankYouReferrer(referrer)) {
      return true;
    }

    return false;
  }

  function isCheckoutCompletedReturn() {
    const referrer = document.referrer || "";
    const location = `${window.location.pathname}${window.location.search}`;

    return isThankYouReferrer(referrer) || isThankYouReferrer(location);
  }

  function clearCheckoutPendingState(options) {
    writeStoredPending(null);
    writeStoredSnapshotRaw(null);
    markCheckoutNavigationForward();
    clearWarehouseCheckoutNavigation();

    if (options?.remainingBaseline) {
      setBaselineFullCart(options.remainingBaseline);
      return;
    }

    if (options?.clearBaseline) {
      clearBaselineFullCart();
    }
  }

  const CHECKOUT_NAV_MODE_KEY = "azparts:checkout-nav-mode";

  function isLikelyAbandonedCheckoutReturn() {
    const nav = performance.getEntriesByType("navigation")[0];
    if (nav && nav.type === "back_forward") {
      return true;
    }

    return sessionStorage.getItem(CHECKOUT_NAV_MODE_KEY) === "back";
  }

  function markCheckoutNavigationBack() {
    sessionStorage.setItem(CHECKOUT_NAV_MODE_KEY, "back");
  }

  function markCheckoutNavigationForward() {
    sessionStorage.removeItem(CHECKOUT_NAV_MODE_KEY);
  }

  function cartHasItemsOutsideTarget(cart, targetItems) {
    const targetMap = new Map(
      normalizeSnapshotItems(targetItems).map(function (item) {
        return [item.id, item.quantity];
      }),
    );

    return (cart?.items || []).some(function (item) {
      return !targetMap.has(item.variant_id);
    });
  }

  function cartSatisfiesTargetItems(cart, targetItems) {
    const targetMap = new Map(
      normalizeSnapshotItems(targetItems).map(function (item) {
        return [item.id, item.quantity];
      }),
    );
    const cartMap = buildCartQuantityMap(cart);

    if (!targetMap.size) return false;

    for (const [variantId, quantity] of targetMap.entries()) {
      if ((cartMap.get(variantId) || 0) < quantity) {
        return false;
      }
    }

    return true;
  }

  function cartMissingCheckoutItems(cart, checkoutItems) {
    const cartMap = buildCartQuantityMap(cart);

    return normalizeSnapshotItems(checkoutItems).some(function (item) {
      return (cartMap.get(item.id) || 0) < item.quantity;
    });
  }

  function resolvePendingCheckoutRestorePlan(cart, snapshot, options) {
    const abandonedReturn =
      options?.abandonedReturn === true || isCheckoutAbandonedReturn();
    const completedReturn =
      options?.completedReturn === true || isCheckoutCompletedReturn();
    const fullCart = getEffectiveFullCart(snapshot);
    const checkoutItems = getSnapshotCheckoutItems(snapshot);
    const remainingItems = getRemainingItemsFromSnapshot(snapshot, fullCart);
    const cartMap = buildCartQuantityMap(cart);

    if (cartMatchesTargetItems(cart, fullCart)) {
      return { mode: "clear" };
    }

    const checkoutItemsInCart = checkoutItems.some(function (item) {
      return (cartMap.get(item.id) || 0) > 0;
    });

    // Back / cancel checkout → always restore the full multi-warehouse snapshot.
    if (abandonedReturn) {
      return { mode: "restore_full", items: fullCart };
    }

    if (remainingItems.length) {
      if (!checkoutItemsInCart && cartMatchesTargetItems(cart, remainingItems)) {
        if (completedReturn) {
          return { mode: "clear", remainingBaseline: remainingItems };
        }

        // Cart looks like "other warehouse only" but we did not come from thank-you —
        // treat as abandoned partial state (common when alternating checkout/back).
        return { mode: "restore_full", items: fullCart };
      }

      if (completedReturn) {
        return {
          mode: "restore_remaining",
          items: remainingItems,
          remainingBaseline: remainingItems,
        };
      }

      return { mode: "restore_full", items: fullCart };
    }

    if (!cart.items.length && !checkoutItemsInCart) {
      return { mode: "clear", clearBaseline: true };
    }

    if (checkoutItemsInCart && completedReturn) {
      return { mode: "restore_remaining", items: [], clearBaseline: true };
    }

    if (checkoutItemsInCart) {
      return { mode: "restore_full", items: fullCart };
    }

    if (fullCart.length && !cartMatchesTargetItems(cart, fullCart)) {
      if (cart.items.length && cartHasItemsOutsideTarget(cart, fullCart)) {
        return { mode: "clear" };
      }

      return { mode: "restore_full", items: fullCart };
    }

    return { mode: "clear" };
  }

  function getSnapshotFullCart(snapshot) {
    return normalizeSnapshotItems(snapshot?.fullCart || snapshot?.items || []);
  }

  function getSnapshotCheckoutItems(snapshot) {
    return normalizeSnapshotItems(snapshot?.checkoutItems || []);
  }

  function getRemainingItemsFromSnapshot(snapshot, fullCartOverride) {
    const fullCart = fullCartOverride || getSnapshotFullCart(snapshot);
    const checkoutItems = getSnapshotCheckoutItems(snapshot);
    const checkoutMap = new Map(checkoutItems.map(function (item) {
      return [item.id, item.quantity];
    }));

    return fullCart
      .map(function (item) {
        const checkoutQuantity = checkoutMap.get(item.id) || 0;
        const remainingQuantity = item.quantity - checkoutQuantity;

        if (remainingQuantity <= 0) return null;

        return {
          id: item.id,
          quantity: remainingQuantity,
        };
      })
      .filter(Boolean);
  }

  function buildCartQuantityMap(cart) {
    const quantityByVariant = new Map();

    (cart?.items || []).forEach(function (item) {
      quantityByVariant.set(
        item.variant_id,
        (quantityByVariant.get(item.variant_id) || 0) + item.quantity,
      );
    });

    return quantityByVariant;
  }

  function cartMatchesTargetItems(cart, targetItems) {
    const targetMap = new Map(
      normalizeSnapshotItems(targetItems).map(function (item) {
        return [item.id, item.quantity];
      }),
    );
    const cartMap = buildCartQuantityMap(cart);

    if (targetMap.size !== cartMap.size) return false;

    for (const [variantId, quantity] of targetMap.entries()) {
      if (cartMap.get(variantId) !== quantity) return false;
    }

    return true;
  }

  async function setCartExactItems(targetItems) {
    const normalizedItems = normalizeSnapshotItems(targetItems);
    const cartUrl = window.Shopify?.routes?.cart_url || "/cart";

    // Fetch current cart to know which variants already exist
    const currentCartResponse = await fetch(`${cartUrl}.js`, { cache: "no-store" });
    const currentCart = currentCartResponse.ok ? await currentCartResponse.json() : { items: [] };
    const currentItems = currentCart.items || [];

    const targetMap = new Map(normalizedItems.map(function (item) {
      return [item.id, item.quantity];
    }));
    const currentVariantIds = new Set(currentItems.map(function (item) {
      return item.variant_id;
    }));

    // Build updates object: set correct quantities for items in cart, zero out non-targets.
    // Using update.js SETS quantities (overwrites), unlike add.js which accumulates.
    // This works even when Shopify has an active checkout that prevents clear.js from working.
    const updates = {};
    currentItems.forEach(function (item) {
      const targetQty = targetMap.get(item.variant_id);
      updates[item.variant_id] = targetQty !== undefined ? targetQty : 0;
    });

    if (Object.keys(updates).length > 0) {
      const updateResponse = await fetch(`${cartUrl}/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates }),
      });

      if (!updateResponse.ok) {
        throw new Error(`Unable to update cart quantities (${updateResponse.status})`);
      }
    }

    if (!normalizedItems.length) return;

    // Add variants that are not yet in the cart (update.js can only update existing line items)
    const itemsToAdd = normalizedItems.filter(function (item) {
      return !currentVariantIds.has(item.id);
    });

    if (!itemsToAdd.length) return;

    const addResponse = await fetch(`${cartUrl}/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: itemsToAdd }),
    });

    if (!addResponse.ok) {
      throw new Error(`Unable to add missing cart items (${addResponse.status})`);
    }
  }

  function hasPendingCheckout() {
    return Boolean(readStoredPending());
  }

  async function fetchCartItemsFromApi() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.cart_url || "/cart"}.js`, {
        cache: "no-store",
      });
      if (!response.ok) return [];

      const cart = await response.json();
      return (cart.items || []).map(function (item) {
        return { id: item.variant_id, quantity: item.quantity };
      });
    } catch (error) {
      return [];
    }
  }

  async function saveWarehouseCheckoutSnapshot(warehouseKey, checkoutItems) {
    const normalizedCheckoutItems = normalizeSnapshotItems(checkoutItems);
    const previousSnapshot = hasPendingCheckout() ? getWarehouseCheckoutSnapshot() : null;
    const previousFullCart = previousSnapshot ? getSnapshotFullCart(previousSnapshot) : [];
    const apiCart = await fetchCartItemsFromApi();
    let mergedFullCart = mergeCartItemLists(apiCart, getBaselineFullCart(), previousFullCart);

    // Prefer cart.js over DOM — grouped cart HTML can duplicate line nodes while resolving.
    if (!apiCart.length) {
      const domCart = collectCartItemsFromDom();
      mergedFullCart = mergeCartItemLists(domCart, mergedFullCart);
    }

    rememberBaselineFullCart(mergedFullCart);

    saveCheckoutSnapshot({
      fullCart: mergedFullCart,
      checkoutItems: normalizedCheckoutItems,
      checkoutWarehouseKey: warehouseKey,
    });
  }

  async function startWarehouseCheckout(checkoutButton, checkoutUrl, warehouseKey, checkoutItems) {
    if (checkoutButton.dataset.checkoutInFlight === "true") return;

    checkoutButton.dataset.checkoutInFlight = "true";
    checkoutButton.setAttribute("aria-disabled", "true");
    checkoutButton.classList.add("button--disabled");

    try {
      await waitForRestoreToFinish();
      await saveWarehouseCheckoutSnapshot(warehouseKey, checkoutItems);

      if (!getWarehouseCheckoutSnapshot()) {
        throw new Error("Unable to capture cart snapshot before warehouse checkout");
      }

      markCheckoutNavigationForward();
      markWarehouseCheckoutNavigation();
      markCheckoutPending();
      window.location.assign(checkoutUrl);
    } catch (error) {
      console.warn("Unable to start warehouse checkout", error);
      delete checkoutButton.dataset.checkoutInFlight;
      checkoutButton.removeAttribute("aria-disabled");
      checkoutButton.classList.remove("button--disabled");
    }
  }

  function saveWarehouseCheckoutSnapshotFromDom(warehouseKey) {
    const previousSnapshot = getWarehouseCheckoutSnapshot();
    const previousFullCart = previousSnapshot ? getSnapshotFullCart(previousSnapshot) : [];
    const domCart = collectCartItemsFromDom();
    const checkoutItems = getWarehouseCheckoutItemsFromDom(warehouseKey);

    saveCheckoutSnapshot({
      fullCart: mergeCartItemLists(domCart, previousFullCart),
      checkoutItems,
      checkoutWarehouseKey: warehouseKey,
    });
  }

  function isCheckoutOrThankYouPage() {
    const path = `${window.location.pathname}${window.location.search}`;
    const host = window.location.hostname;

    if (/checkout/i.test(host) || /\/checkouts\//i.test(path)) {
      return true;
    }

    return /thank[-_]?you|order[-_]?status|post_purchase|orders\//i.test(path);
  }

  function getWarehouseCheckoutItemsFromDom(warehouseKey) {
    const domItems = collectCartItemsFromDom(warehouseKey);
    if (domItems.length) return domItems;

    const checkoutButton = document.querySelector(
      `a.cart-warehouse-checkout-button[data-warehouse-checkout="${warehouseKey}"]`,
    );
    if (checkoutButton?.href) {
      return parseWarehouseCheckoutItems(checkoutButton.href);
    }

    return [];
  }

  function saveFullCartCheckoutSnapshotFromDom() {
    const fullCart = collectCartItemsFromDom();

    saveCheckoutSnapshot({
      fullCart,
      checkoutItems: fullCart,
      checkoutWarehouseKey: "",
    });
  }

  function getWarehouseCheckoutSnapshot() {
    const rawSnapshot = readStoredSnapshotRaw();
    if (!rawSnapshot) return null;

    try {
      const snapshot = JSON.parse(rawSnapshot);
      if (!getSnapshotFullCart(snapshot).length) {
        clearCheckoutPendingState();
        return null;
      }

      if (Date.now() - (snapshot.savedAt || 0) > 2 * 60 * 60 * 1000) {
        clearCheckoutPendingState();
        return null;
      }

      return snapshot;
    } catch (error) {
      clearCheckoutPendingState();
      return null;
    }
  }

  const CART_UI_REFRESH_FOLLOWUP_MS = 350;

  async function refreshCartSectionsAfterRestore() {
    document.dispatchEvent(new CustomEvent("cart:refresh"));

    const miniCart = document.querySelector("mini-cart");
    if (miniCart && typeof miniCart.updateSectionContents === "function") {
      await miniCart.updateSectionContents();
    }

    const cartItems = document.querySelector("cart-items");
    if (cartItems && typeof cartItems.updateSectionContents === "function") {
      await cartItems.updateSectionContents();
    }

    if (typeof window.resolveCartWarehouses === "function") {
      await window.resolveCartWarehouses();
    } else if (window.CartQuantityLimit) {
      window.CartQuantityLimit.applyCartQuantityLimitUIFromDom(document);
    }
  }

  async function scheduleCartUiRefreshAfterRestore() {
    await refreshCartSectionsAfterRestore();

    // Mini-cart runs updateSectionContents() on connect; that fetch can finish after
    // restore and overwrite the header/drawer with stale empty-cart HTML. Re-fetch once.
    window.setTimeout(function () {
      refreshCartSectionsAfterRestore().catch(function (error) {
        console.warn("Unable to follow-up refresh cart UI after warehouse checkout restore", error);
      });
    }, CART_UI_REFRESH_FOLLOWUP_MS);
  }

  async function restoreWarehouseCheckoutItems() {
    if (restoreCheckoutInFlight) {
      return false;
    }

    const snapshot = getWarehouseCheckoutSnapshot();
    if (!snapshot || !hasPendingCheckout()) {
      return false;
    }

    restoreCheckoutInFlight = true;

    try {
      const cartResponse = await fetch(`${window.Shopify?.routes?.cart_url || "/cart"}.js`, {
        cache: "no-store",
      });
      if (!cartResponse.ok) return false;

      const cart = await cartResponse.json();
      const plan = resolvePendingCheckoutRestorePlan(cart, snapshot, {
        abandonedReturn: isCheckoutAbandonedReturn(),
        completedReturn: isCheckoutCompletedReturn(),
      });

      if (plan.mode === "clear") {
        clearCheckoutPendingState({
          remainingBaseline: plan.remainingBaseline,
          clearBaseline: plan.clearBaseline,
        });
        return true;
      }

      await setCartExactItems(plan.items);

      if (plan.mode === "restore_full") {
        rememberBaselineFullCart(plan.items);
      }

      clearCheckoutPendingState({
        remainingBaseline: plan.remainingBaseline,
        clearBaseline: plan.clearBaseline,
      });
      await scheduleCartUiRefreshAfterRestore();
      return true;
    } finally {
      restoreCheckoutInFlight = false;
    }
  }

  function scheduleCartRestoreOnce() {
    if (restoreScheduled) return;

    restoreScheduled = true;

    maybeRestoreWarehouseCheckoutItems().catch(function (error) {
      console.warn("Unable to restore cart after warehouse checkout", error);
    });
  }

  async function maybeRestoreWarehouseCheckoutItems() {
    if (isCheckoutOrThankYouPage()) return;
    if (shouldSkipRestoreDuringCheckoutNavigation()) return;
    if (!hasPendingCheckout() || !getWarehouseCheckoutSnapshot()) return;

    const snapshot = getWarehouseCheckoutSnapshot();
    if (!snapshot) return;

    try {
      const cartResponse = await fetch(`${window.Shopify?.routes?.cart_url || "/cart"}.js`, {
        cache: "no-store",
      });

      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        const remainingItems = getRemainingItemsFromSnapshot(
          snapshot,
          getEffectiveFullCart(snapshot),
        );
        const checkoutItems = getSnapshotCheckoutItems(snapshot);
        const checkoutItemsInCart = checkoutItems.some(function (item) {
          return (buildCartQuantityMap(cart).get(item.id) || 0) > 0;
        });
        const abandonedReturn = isCheckoutAbandonedReturn();
        const completedReturn = isCheckoutCompletedReturn();

        if (
          !abandonedReturn &&
          completedReturn &&
          remainingItems.length &&
          !checkoutItemsInCart &&
          cartMatchesTargetItems(cart, remainingItems)
        ) {
          clearCheckoutPendingState({ remainingBaseline: remainingItems });
          return;
        }

        if (abandonedReturn) {
          await restoreWarehouseCheckoutItems();
          return;
        }

        if (
          !completedReturn &&
          remainingItems.length &&
          (checkoutItemsInCart || !cartMatchesTargetItems(cart, remainingItems))
        ) {
          await restoreWarehouseCheckoutItems();
          return;
        }
      }
    } catch (error) {
      console.warn("Unable to pre-check cart before warehouse checkout restore", error);
    }

    await restoreWarehouseCheckoutItems();
  }

  document.addEventListener(
    "click",
    function (event) {
      const checkoutButton = event.target.closest("a.cart-warehouse-checkout-button[data-warehouse-checkout]");
      if (!checkoutButton || checkoutButton.classList.contains("button--disabled")) return;

      event.preventDefault();

      const warehouseKey = checkoutButton.dataset.warehouseCheckout;
      const checkoutItems = parseWarehouseCheckoutItems(checkoutButton.href);
      const checkoutUrl = checkoutButton.href;

      startWarehouseCheckout(checkoutButton, checkoutUrl, warehouseKey, checkoutItems).catch(
        function (error) {
          console.warn("Unable to start warehouse checkout", error);
        },
      );
    },
    true,
  );

  document.addEventListener(
    "click",
    function (event) {
      const checkoutButton = event.target.closest(
        "#checkout:not(.button--disabled), button[name='checkout']:not(.button--disabled):not([disabled])",
      );
      if (!checkoutButton || checkoutButton.disabled) return;

      saveFullCartCheckoutSnapshotFromDom();
      markCheckoutPending();
    },
    true,
  );

  document.addEventListener(
    "submit",
    function (event) {
      const form = event.target.closest('form[action*="/cart"]');
      if (!form) return;

      const submitter = event.submitter;
      if (submitter?.name !== "checkout" || submitter.disabled) return;

      saveFullCartCheckoutSnapshotFromDom();
      markCheckoutPending();
    },
    true,
  );

  document.addEventListener("visibilitychange", function () {
    if (document.hidden) return;
    if (!hasPendingCheckout() || !getWarehouseCheckoutSnapshot()) return;

    // Avoid re-running restore when the shopper is just switching tabs on a cart page
    // that already holds the remaining warehouse items.
    if (isCartLikePage()) {
      fetch(`${window.Shopify?.routes?.cart_url || "/cart"}.js`, { cache: "no-store" })
        .then(function (response) {
          if (!response.ok) return null;
          return response.json();
        })
        .then(function (cart) {
          if (!cart?.items?.length) {
            restoreScheduled = false;
            scheduleCartRestoreOnce();
            return;
          }

          const snapshot = getWarehouseCheckoutSnapshot();
          if (!snapshot) return;

          const effectiveFullCart = getEffectiveFullCart(snapshot);
          const remainingItems = getRemainingItemsFromSnapshot(snapshot, effectiveFullCart);
          const checkoutItems = getSnapshotCheckoutItems(snapshot);
          const checkoutItemsInCart = checkoutItems.some(function (item) {
            return (buildCartQuantityMap(cart).get(item.id) || 0) > 0;
          });
          const abandonedReturn = isCheckoutAbandonedReturn();
          const completedReturn = isCheckoutCompletedReturn();

          if (
            !abandonedReturn &&
            completedReturn &&
            remainingItems.length &&
            !checkoutItemsInCart &&
            cartMatchesTargetItems(cart, remainingItems)
          ) {
            clearCheckoutPendingState({ remainingBaseline: remainingItems });
            return;
          }

          if (abandonedReturn || !cart?.items?.length) {
            restoreScheduled = false;
            scheduleCartRestoreOnce();
            return;
          }

          if (
            !completedReturn &&
            remainingItems.length &&
            (checkoutItemsInCart || !cartMatchesTargetItems(cart, remainingItems))
          ) {
            restoreScheduled = false;
            scheduleCartRestoreOnce();
            return;
          }

          restoreScheduled = false;
          scheduleCartRestoreOnce();
        })
        .catch(function () {
          restoreScheduled = false;
          scheduleCartRestoreOnce();
        });
      return;
    }

    restoreScheduled = false;
    scheduleCartRestoreOnce();
  });

  window.addEventListener("pageshow", function (event) {
    if (event.persisted) {
      markCheckoutNavigationBack();
      restoreScheduled = false;
    }

    scheduleCartRestoreOnce();
  });

  document.addEventListener("DOMContentLoaded", function () {
    scheduleCartRestoreOnce();
  });
})();
